import { AfterViewInit, ChangeDetectorRef, Component, OnInit, signal, ViewChild } from '@angular/core';
import { DialogRef } from '@fundamental-ngx/core/dialog';
import { NotificationsService } from '../../../../service/services/misc/notifications/notifications.service';
import { KeyValueModel } from '../../../../core/models/misc/key-value/key-value.model';
import {Const, PolicyAction} from '../../../../core/helpers/Const';
import { AddLabelsComponent } from '../../../../shared/components/add-labels/add-labels.component';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { CustomValidators } from '../../../../core/helpers/CustomValidators';
import { Utils } from '../../../../core/helpers/Utils';
import {
    AvailableInstanceTypesModel,
    RTDModel
} from '../../../../core/models/resources/rtd.model';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { FormlyJsonschema } from '@ngx-formly/core/json-schema';
import { ResourceDataService } from '../../../../service/services/rest/resource-data/resource-data.service';
import { SideDialogComponent } from '../../../../shared/components/side-dialog/side-dialog.component';
import { ResourceModel } from '../../../../core/models/resources/resource.model';
import {DialogService} from "../../../../service/services/misc/dialog/dialog.service";
import {BaseCreateDialogComponent} from "../../../../core/components/abstract/base-create-dialog/base-create-dialog.component";
import {SwaService} from "../../../../service/services/misc/swa/swa.service";
import {UisService} from "../../../../service/services/misc/uis/uis.service";
import {firstValueFrom, tap} from "rxjs";
import {FormStates} from "@fundamental-ngx/cdk/forms";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {MessageBoxService} from "@fundamental-ngx/core";

@Component({
    selector: 'app-create-instance-side-dialog',
    templateUrl: './create-instance-dialog.component.html',
    styleUrls: ['./create-instance-dialog.component.scss'],
    standalone: false
})
export class CreateInstanceDialogComponent extends BaseCreateDialogComponent implements OnInit, AfterViewInit {
    @ViewChild('dialog') private dialog: SideDialogComponent;
    Const = Const;
    serviceInstancesByGroup: any[];
    selectedResource: ResourceModel;
    resourceCreationTypeState: FormStates;
    allServices: AvailableInstanceTypesModel;

    model: any = {};
    fields: FormlyFieldConfig[];
    options: FormlyFormOptions = {};
    formlyForm: UntypedFormGroup;

    isPoliciesLoading = signal(false);

    override form = this.fb.group({
	    name: [
	        { value: '', disabled: true },
	        { validators: [Validators.required, CustomValidators.ValidateTechnicalName] },
	    ],
	    managedService: ['', { validators: [Validators.required] }],
	    group: [{ value: '', disabled: true }, { validators: [Validators.required] }],
	    version: [{ value: '', disabled: true }, { validators: [Validators.required] }],
	    path: [{ value: '', disabled: true }, { validators: [] }],
    });


    @ViewChild('addLabelsComponent')
	    addLabelsComponent: AddLabelsComponent;

    @ViewChild('addAnnotationsComponent')
	    addAnnotationsComponent: AddLabelsComponent;

    constructor(
		public override dialogRef: DialogRef,
    	public override dialogService: DialogService,

		protected override notificationsService: NotificationsService,
		private resourceDataService: ResourceDataService,
		protected override fb: UntypedFormBuilder,
		protected override cdRef: ChangeDetectorRef,
		private formlyJsonschema: FormlyJsonschema,
    	protected override _swaService: SwaService,
		protected override _uisService: UisService,
		private messageBoxService: MessageBoxService
    ) {
	    super(dialogRef, dialogService, notificationsService, fb, cdRef, _swaService, _uisService);
    }

    get name() {
	    return this.form.controls['name'];
    }

    get managedService() {
	    return this.form.controls['managedService'];
    }

    override ngOnInit() {
	    const data = this.dialogRef.data;
	    this.isEdit = data.isEdit;
	    this.constraintType = this.isEdit ? 'edit' : 'create';
	    this.selectedResource = this.isEdit ? data.resource : data.selectedResource;
	    this.resourceCreationTypeState = 'default';
	    this.formlyForm = new UntypedFormGroup({});
	    this.resourceName = this.getResourceName();
	    this.resourceDisplayName = this.getResourceDisplayName();

        if (this.selectedResource) {
            this.onServiceChange(this.selectedResource, this.isSystemResource(this.selectedResource.type));
            this.initSubresourceEnablement(this.selectedResource.rules);
        } else {
            void this.loadServices();
        }

	    if (this.isEdit) {
	        let schema = data.schema;
	        let resource = data.resource;
	        this.model = this.loadModelResourceData(this.deleteYamlFields(resource.resource));
	        this.loadSourceForEdit(resource, schema);
	    } else {
	        this.labels = [new KeyValueModel('', '')];
	        this.annotations = [new KeyValueModel('', '')];
	    }

        this.subscribeToEvents();

	    this._swaService.sendCustomEvent(
	        this.isEdit? this._swaService.SWA_EVENT_TYPES.OPEN_EDIT_RESOURCE_FORM :
	            this._swaService.SWA_EVENT_TYPES.OPEN_CREATE_RESOURCE_FORM,
	        [this.isEdit ? "Service Instance - " + data?.resource?.type : "Service Instance"],
	        []
	    );
    }

    ngAfterViewInit(): void {
	    if (this.isEdit) {
	        this.dialogRef.data.resource.resource = this.deleteYamlFields(this.dialogRef.data.resource?.resource);
	    }
    }

    async loadServices() {
        const path = this.dialogRef.data.path;
        const type = this.dialogRef.data.type;
        this.setLoading(true);
        try {
            this.allServices = await firstValueFrom(this.resourceDataService.getAvailableInstanceTypes(`${type}/${path}`));
            if (!this.allServices?.instances?.length) {
                const messageBoxRef = this.dialogService.openMessageBox("Cannot Create New Resource", "You do not have permissions to create an instance of any resource type in this location. Please contact your administrator." ,"error", "440px");
                await firstValueFrom(messageBoxRef.afterClosed);
                this.dialogRef.close();
                return;
            }
            this.serviceInstancesByGroup = [...this.allServices.instances].sort(this.sortFn);
            this.cdRef.detectChanges();
            try { this.dialog.focusErrorStrip(); } catch {}
        } catch (e) {
            this.errorMsg = 'Failed to load available resource types. Please try again later.';
            this.cdRef.detectChanges();
            try { this.dialog.focusErrorStrip(); } catch {}
        } finally {
            this.setLoading(false);
        }
    }

    sortFn(a: RTDModel, b: RTDModel) {
        if (a.type < b.type) {
            return -1;
        }
        if (a.type > b.type) {
            return 1;
        }
        return 0;
    }

    subscribeToEvents() {
        this.managedService.valueChanges.pipe(
            debounceTime(100),
            distinctUntilChanged()
        ).subscribe(async value => {
            await this.initPermissions(value);
            this.onServiceChange(value);
        });
    }

    loadModelResourceData(resource: any) {
	    let resourceCopy = JSON.parse(JSON.stringify(resource));
	    delete resourceCopy.metadata;
	    delete resourceCopy.apiVersion;
	    delete resourceCopy.type;
	    return resourceCopy;
    }

    loadSourceForEdit(resource: ResourceModel, schema: any) {
	    let groupVal = resource.apiVersion.substring(0, resource.apiVersion.indexOf('/'));
	    let version = resource.apiVersion.substring(resource.apiVersion.indexOf('/') + 1, resource.apiVersion.length);
	    let group = { group: groupVal, item: { versions: [{ name: version, schema: schema }] } };
	    this.form.get('name').setValue(resource.name);
	    this.form
	        .get('managedService')
	        .setValue({ group: groupVal, type: resource.type, versions: [{ name: version }] }, { emitEvent: false });
	    if (!this.isSystemResource(resource.type)) {
	        this.form.controls['managedService'].disable({ onlySelf: true, emitEvent: false });
	    }
	    this.form.get('group').setValue(groupVal);
	    this.onGroupChange(group);
	    this.loadResourceMetadata(resource);
    }

    isSystemResource(resourceType: string) {
	    return Const.SYSTEM_RESOURCES.includes(resourceType);
    }

    loadResourceMetadata(resource: ResourceModel) {
	    if (resource.labels) {
	        this.labels = Object.keys(resource.labels).map((key) => new KeyValueModel(key, resource.labels[key]));
	    }
	    if (resource.annotations) {
	        this.annotations = Object.keys(resource.annotations).map(
	            (key) => new KeyValueModel(key, resource.annotations[key])
	        );
	    }
	    if (this.labels.length === 0) {
	        this.labels.push(new KeyValueModel('', ''));
	    }
	    if (this.annotations.length === 0) {
	        this.annotations.push(new KeyValueModel('', ''));
	    }
	    this.form.get('path').setValue(resource.path);
    }

    onServiceChange(service: any, isCoreService?: boolean) {
        if (!service) {
            return;
        }
	    if (!isCoreService && !this.isEdit) {
	        this.selectedResource = service;
	    }
	    this.resetName();
	    let serviceGroup = service?.group;
	    let versions = service?.versions;
	    if (isCoreService) {
	        this.form.get('managedService').setValue(service);
	    }
	    this.form.get('group').setValue(serviceGroup);
	    this.setVersions(versions);
	    this.errorMsg = '';
    }

    async initPermissions(service: any): Promise<void> {
        this.isPoliciesLoading.set(true);
        try {
            const policies = await this.retrievePolicies(service);
            this.initSubresourceEnablement(policies);
            const hasPermission = policies.some(policy => policy.action === "create" || policy.action === "*");
            this.resourceCreationTypeState = hasPermission ? 'default' : 'error';
        } finally {
            this.isPoliciesLoading.set(false);
        }
    }

    async retrievePolicies(resource: any) {
        const policiesObservable = this.resourceDataService.getPolicies(
            this.dialogRef.data.path,
            [{ group: resource.group, type: resource.type }, { group: resource.group, type: "*/*" }]
        );

        let policies = await firstValueFrom(policiesObservable);
        return policies.filter(policy => {
            const type = policy.type.split('/')[0];
            //Check for a specific type or '*' for all types (opscurity scenario)
            return type === resource.type || type === "*";
        });
    }

    setVersions(versions: any[]) {
	    if (versions?.length > 1 && !this.isEdit) {
	        this.form.controls['version'].enable({ onlySelf: true });
	    } else {
	        this.form.controls['version'].disable({ onlySelf: true });
	    }
	    if (versions?.length === 1) {
	        this.form.get('version').setValue(versions[0]);
	        this.onVersionChange({ item: versions[0] });
	    } else {
	        this.form.get('version').setValue(undefined);
	        this.onVersionChange(undefined);
	    }
    }

    onGroupChange(group: any) {
	    if (group?.item?.versions?.length > 0 && !this.isEdit) {
	        this.form.controls['version'].enable({ onlySelf: true });
	    } else {
	        this.form.controls['version'].disable({ onlySelf: true });
	    }
	    if (group?.item?.versions?.length === 1) {
	        this.form.get('version').setValue(group.item.versions[0]);
	        this.onVersionChange({ item: group.item.versions[0] });
	    } else {
	        this.form.get('version').setValue(undefined);
	        this.onVersionChange(undefined);
	    }
    }

    onVersionChange(version: any) {
	    let schema = version?.item?.schema?.jsonSchema;
	    this.resetFormlyForm();
	    this.loadMetadata(schema);
	    this.renderFormBySchema(schema);
	    this.setEmptyValues();

	    if (this.isEdit) {
	        this.form.controls['version'].disable({ onlySelf: true });
	    }

    }

    setEmptyValues() {
	    let spec: any = this.formlyForm.get('spec');
	    if (spec) {
	        Object.values(spec?.controls).forEach((control: any) => {
	            if (control.value === null && control?.errors?.type?.schemaType[0] === 'string') {
	                control.setValue(undefined);
	            }
	        });
	    }
    }
    renderFormBySchema(schema: any) {
	    if (!schema) {
	        return;
	    }
	    let jsonSchema = JSON.parse(JSON.stringify(schema));
	    if (jsonSchema?.properties) {
	        delete jsonSchema.properties.metadata;
	        delete jsonSchema.properties.apiVersion;
	        delete jsonSchema.properties.status;
	        delete jsonSchema.properties.type;

	        this.adjustSchemaEnablement(jsonSchema);
	        this.fields = [
                this
                    .formlyJsonschema
                    .toFieldConfig(
                        this.replaceObjectWithYamlFields(jsonSchema),
                        { map: (field) => this.minItemsArrayValidator(field) }
                    )
            ];
	        this.cdRef.detectChanges();
	    }
    }

    private minItemsArrayValidator(field: FormlyFieldConfig): FormlyFieldConfig {
        if (field.type === "array" && field.props?.['minItems'] > 0) {
            if (field.validators?.["minItems"]) {
                delete field.validators["minItems"];
            }

            const min = field.props?.["minItems"];
            field.validators = {
                ...(field.validators ?? {}),
                minItems: {
                    expression: (c: AbstractControl) => this.minItemsValidationExpression(c, min),
                },
            };
        }
        return field;
    }

    private minItemsValidationExpression(control: AbstractControl, originalMinItems: number): boolean {
        if (!originalMinItems) {
            return true;
        }

        if (!control.value) {
            return true;
        }

        if (!Array.isArray(control.value)) {
            return true;
        }
        return control.value.length === 0 || control.value.length >= originalMinItems;
    }


    adjustSchemaEnablement(jsonSchema: any) {
	    for (let [key] of Object.entries(jsonSchema.properties)) {
	        this.adjustSubresourceEnablement(jsonSchema.properties[key], key);
	    }
    }

    adjustSubresourceEnablement(prop: any, key: string) {
	    const customSubresource = this.selectedResource?.customSubresources?.find(c => c.jsonPath.substring(1) === key);
	    //If the current property of the schema is not a custom subresource, we check if it should be blocked or not
	    if (!customSubresource) {
	        if (!this.isNonSubresourceActionsEnabled) {
	            this.disableNonSubresourceProperty(prop);
	        }
	        return;
	    }

	    this.adjustSubresourceProperty(customSubresource.name, prop);
    }

    disableNonSubresourceProperty(prop: any) {
	    prop.readOnly = true;
	    prop.widget = {
	        "formlyConfig": {
	            "templateOptions": {
	                "overlayTooltip": "You do not have permission to edit these properties in the resource"
	            }
	        }
	    };
    }

    disableCustomSubresourceProperty(prop: any) {
	    prop.readOnly = true;
	    prop.widget = {
	        "formlyConfig": {
	            "templateOptions": {
	                "overlayTooltip": "You do not have permission to edit the properties in this subresource"
	            }
	        }
	    };
    }

    adjustSubresourceProperty(customSubresourceName: string, prop: any) {
	    const hasSubresourceUpdateRules = this.subresourceRules?.filter(r => {
	        const subresourceName = r.type.split("/")[1];
	        return subresourceName === customSubresourceName || subresourceName === PolicyAction.All;
	    }).some(r => r.action === PolicyAction.Update || r.action === PolicyAction.Patch || r.action === PolicyAction.All);

	    if (!hasSubresourceUpdateRules) {
	        this.disableCustomSubresourceProperty(prop);
	    }
    }



    loadMetadata(schema: any) {
	    let schemaLabels = schema?.properties?.metadata?.properties?.labels;
	    let schemaAnnotations = schema?.properties?.metadata?.properties?.annotations;
	    if (schemaLabels) {
	        this.labels = Object.keys(schemaLabels).map((key) => new KeyValueModel(key, schemaLabels[key]));
	    }
	    if (schemaAnnotations) {
	        this.annotations = Object.keys(schemaAnnotations).map(
	            (key) => new KeyValueModel(key, schemaAnnotations[key])
	        );
	    }
	    this.form.get('path').setValue(this.dialogRef.data.path);
    }

    isLabelsEmpty(labels: KeyValueModel[]) {
	    return !labels || labels.length === 0 || (labels.length === 1 && !labels[0].key.name);
    }

    isServiceComboVisible() {
	    if (!this.isEdit) {
	        return this.serviceInstancesByGroup;
	    } else {
	        let resource = this.dialogRef.data.resource;
	        return !this.isSystemResource(resource?.type);
	    }
    }

    resetName() {
	    this.form.get('name').setValue(undefined);
	    // when changing a service, reset the name field, and markAsPending -> the input field is not marked in red(required)
	    this.form.get('name').markAsPending({ onlySelf: true });
    }

    resetFormlyForm() {
	    this.formlyForm?.reset();
	    this.options?.resetModel?.();
	    this.labels = [new KeyValueModel('', '')];
	    this.annotations = [new KeyValueModel('', '')];
	    this.formlyForm = new UntypedFormGroup({});
	    this.fields = [];
    }

    override onSubmit() {
	    this.setLoading(true);
	    let instance: any = this.convertYamlFieldsToJson(this.model, '', this.model);
	    let metadata: any = {};
	    if (this.isEdit) {
	        instance.apiVersion = this.dialogRef.data?.resource?.apiVersion;
	        instance.type = this.dialogRef.data?.resource?.type;
	        metadata = this.dialogRef.data?.resource?.resource?.metadata;
	        metadata.path = this.dialogRef.data?.resource?.path;
	        this._swaService.sendCustomEvent( this._swaService.SWA_EVENT_TYPES.EDIT_RESOURCE_WITH_FORM, [instance.type],[]);
	    } else {
	        let group = this.form.get('group').value;
	        let version = this.form.get('version').value;
	        instance.apiVersion = group + '/' + version?.name;
	        instance.type = this.selectedResource.type;
	        metadata.path = this.dialogRef.data.path;
	        this._swaService.sendCustomEvent( this._swaService.SWA_EVENT_TYPES.CREATE_RESOURCE_WITH_FORM, [instance.type],[]);
	    }
	    metadata.name = this.name.value;
	    this.setKeyValues(metadata);


	    instance.metadata = metadata;

	    let resourceDataMethod;
	    if (!this.isNonSubresourceActionsEnabled) {
	        resourceDataMethod = this.resourceDataService.updateSubresources({ parameters: instance });
	    } else {
	        resourceDataMethod = this.isEdit
	            ? this.resourceDataService.update('', '', { parameters: instance })
	            : this.resourceDataService.add('', '', { parameters: instance });

	    }
	    resourceDataMethod
	        .subscribe({
	            next: this.onSuccess.bind(this),
	            error: this.onError.bind(this, instance)
	        })
	        .add(() => {
	            this.setLoading(false);
	        });
    }

    override onError(instance: any, error: Error) {
	    this.errorMsg = error.message;

	    this._uisService.sendUISEvent((this.isEdit ? this._uisService.UIS_EVENT_TYPES.EDIT_RESOURCE_WITH_FORM_FAILED :
	        this._uisService.UIS_EVENT_TYPES.CREATE_RESOURCE_WITH_FORM_FAILED) + " - " + instance.type,{type: instance.type, errorMsg:this.errorMsg});

	    this._swaService.sendCustomEvent((this.isEdit ? this._swaService.SWA_EVENT_TYPES.EDIT_RESOURCE_WITH_FORM_FAILED :
	        this._swaService.SWA_EVENT_TYPES.CREATE_RESOURCE_WITH_FORM_FAILED) + " - " + instance.type, [this.errorMsg], []);

	    this.setLoading(false);
	    this.cdRef.detectChanges();
	    this.dialog.focusErrorStrip();
    }

    private setKeyValues(metadata: any) {
	    metadata.labels = Utils.convertLabels(this.labels);
	    if (Object.keys(metadata.labels).length === 0) {
	        delete metadata.labels;
	    }
	    metadata.annotations = Utils.convertLabels(this.annotations);
	    if (Object.keys(metadata.annotations).length === 0) {
	        delete metadata.annotations;
	    }
    }

    override onSuccess(res: any) {
	    const msg = (this.isEdit ? 'Updating instance ' : 'Creating instance ') + res.name + ' (' + res.type + ')...';
	    this.notificationsService.showMessageToast(msg);
	    this.dialogRef.close(res);

	    this._uisService.sendUISEvent((this.isEdit ? this._uisService.UIS_EVENT_TYPES.EDIT_RESOURCE_WITH_FORM_SUCCESS :
	        this._uisService.UIS_EVENT_TYPES.CREATE_RESOURCE_WITH_FORM_SUCCESS) + " - " + res.type, {type: res.type, path: res.metadata?.path});

	    this._swaService.sendCustomEvent((this.isEdit ? this._swaService.SWA_EVENT_TYPES.EDIT_RESOURCE_WITH_FORM_SUCCESS :
	        this._swaService.SWA_EVENT_TYPES.CREATE_RESOURCE_WITH_FORM_SUCCESS), [res.type], []);
    }

    displayServiceFunc(assignmentModel: RTDModel): string {
	    return (assignmentModel?.type || '') + (assignmentModel?.type ? ' - ' : '') + (assignmentModel?.group || '');
    }

    displayGroupFunc(group: any) {
	    return group?.group;
    }

    displayVersionFunc(version: any) {
	    return version?.name;
    }

    validateLabels() {
	    if (!this.addLabelsComponent) {
	        return false;
	    }
	    return this.addLabelsComponent.validateLabels();
    }

    override validateForm() {
        const isFormValid = (this.form.valid || this.form.disabled) && this.form.get('version').value != '' && (this.formlyForm.valid || this.formlyForm.disabled) && !this.loading;

        const hasPermissions = this.resourceCreationTypeState !== 'error';

        return isFormValid && hasPermissions;
    }

    getResourceName() {
	    if (this.isEdit) {
	        return this.dialogRef?.data?.resource?.type;
	    } else {
	        return this.selectedResource ? this.selectedResource.type : 'ServiceInstance';
	    }
    }

    getResourceDisplayName() {
	    if (this.isEdit) {
	        return this.dialogRef?.data?.resource?.typeDisplayName || this.dialogRef?.data?.resource?.type;
	    } else {
	        return this.selectedResource ? (this.selectedResource.displayName || this.selectedResource.type) : 'Resource';
	    }
    }
    convertYamlFieldsToJson(model: any, parentKey: string, parentModel: any) {
	    if (!model) {
	        return;
	    }
	    if (model instanceof Object) {
	        Object.keys(model || {}).forEach((key) => {
	            if (key === 'yamlObjectReplace') {
	                parentModel[parentKey] = Utils.parseYamlToJson(model[key]);
	            } else {
	                return this.convertYamlFieldsToJson(model[key], key, model);
	            }
	        });
	    }
	    return model;
    }

    deleteYamlFields(resource: any) {
	    if (!resource) {
	        return;
	    }
	    if (resource instanceof Object) {
	        delete resource.yamlObjectReplace;
	        Object.keys(resource || []).forEach((item) => {
	            this.deleteYamlFields(resource[item]);
	        });
	        return resource;
	    }
    }

    replaceObjectWithYamlFields(schema: any) {
	    if (!schema) {
	        return;
	    }
	    if (schema.type === 'object' && !schema.properties && !schema.hasOwnProperty('oneOf')) {
	        schema.properties = {
	            yamlObjectReplace: {
	                key: 'yamlObjectReplace',
	                type: 'yaml',
	            },
	        };
	    }
	    if (schema.type === 'array') {
	        schema = schema.items;
	    }
	    Object.keys(schema?.properties || []).forEach((item) => {
	        this.replaceObjectWithYamlFields(schema.properties[item]);
	    });
	    return schema;
    }

}
